import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Input,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { CLIENT_FLOW_STORAGE_KEYS, safeParseJson } from "./clientFlowUtils";

const defaultForm = {
  primaryDomain: "",
  callbackUrl: "",
  webhookUrl: "",
  webhookSecret: "",
  stripeAccountId: "",
  notes: "",
};

const ClientIntegrationSettingsPage = () => {
  const toast = useToast();
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = safeParseJson(window.localStorage.getItem(CLIENT_FLOW_STORAGE_KEYS.INTEGRATIONS), defaultForm);
    setForm({ ...defaultForm, ...(stored || {}) });
  }, []);

  const payloadPreview = useMemo(() => JSON.stringify(form, null, 2), [form]);

  const saveDraft = () => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CLIENT_FLOW_STORAGE_KEYS.INTEGRATIONS, JSON.stringify(form));
    toast({ title: "Integration inputs saved", status: "success" });
  };

  const copyJson = async () => {
    if (!navigator?.clipboard?.writeText) {
      toast({ title: "Clipboard not available", status: "warning" });
      return;
    }

    try {
      await navigator.clipboard.writeText(payloadPreview);
      toast({ title: "Copied integration JSON", status: "success" });
    } catch (error) {
      toast({ title: "Copy failed", description: error.message, status: "error" });
    }
  };

  return (
    <Stack spacing={6}>
      <Box>
        <Heading size="md" mb={2}>Integration Configuration</Heading>
        <Text color="gray.600" fontSize="sm">Capture integration input values for onboarding and ongoing service operations.</Text>
      </Box>

      <Alert status="info" borderRadius="md">
        <AlertIcon />
        Draft values are stored locally in this browser for your client flow.
      </Alert>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
        <FormControl>
          <FormLabel>Primary Domain</FormLabel>
          <Input value={form.primaryDomain} onChange={(e) => setForm((prev) => ({ ...prev, primaryDomain: e.target.value }))} />
        </FormControl>
        <FormControl>
          <FormLabel>Callback URL</FormLabel>
          <Input value={form.callbackUrl} onChange={(e) => setForm((prev) => ({ ...prev, callbackUrl: e.target.value }))} />
        </FormControl>
        <FormControl>
          <FormLabel>Webhook URL</FormLabel>
          <Input value={form.webhookUrl} onChange={(e) => setForm((prev) => ({ ...prev, webhookUrl: e.target.value }))} />
        </FormControl>
        <FormControl>
          <FormLabel>Webhook Secret</FormLabel>
          <Input value={form.webhookSecret} onChange={(e) => setForm((prev) => ({ ...prev, webhookSecret: e.target.value }))} />
        </FormControl>
        <FormControl>
          <FormLabel>Stripe Account ID</FormLabel>
          <Input value={form.stripeAccountId} onChange={(e) => setForm((prev) => ({ ...prev, stripeAccountId: e.target.value }))} />
        </FormControl>
      </Grid>

      <FormControl>
        <FormLabel>Implementation Notes</FormLabel>
        <Textarea rows={4} value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} />
      </FormControl>

      <Stack direction={{ base: "column", md: "row" }} spacing={3}>
        <Button colorScheme="blue" onClick={saveDraft}>Save Draft</Button>
        <Button variant="outline" onClick={copyJson}>Copy JSON</Button>
      </Stack>

      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
        <Text fontWeight="semibold" mb={2}>Preview</Text>
        <Box as="pre" m={0} fontSize="sm" whiteSpace="pre-wrap">{payloadPreview}</Box>
      </Box>
    </Stack>
  );
};

export default ClientIntegrationSettingsPage;
